import React, { useEffect, useId, useRef } from 'react';
import { preserveScroll } from '../lib/preserveScroll';
import { revealTabScrollLeft } from '../lib/tabStrip';
import { Icon } from './Icon';
import PlanLockPill from './PlanLockPill';
import type { IconName } from './Icon';

export interface PageTab<K extends string = string> {
    key: K;
    label: string;
    icon?: IconName;
    /** Optional badge count. */
    count?: number;
    /** When true, the tab is rendered greyed-out and clicks are ignored. */
    disabled?: boolean;
    /** A small primary dot after the label: something inside needs a look (a
     *  route that stopped answering, for instance). Not a count — use `count`
     *  when the number itself is the information. */
    dot?: boolean;
    /** A tab the plan does not include: a lock and the cheapest plan that does
     *  («🔒 Pro»). The tab still OPENS — it shows what the plan gives and how
     *  to try it; locking the data is the page's and the server's job. */
    lockLabel?: string;
}

export interface PageTabsProps<K extends string = string> {
    tabs: PageTab<K>[];
    activeTab: K;
    onChange: (key: K) => void;
    /**
     * `sticky` renders a sticky top bar (used in detail pages with a page header above).
     * `inline` keeps normal flow (used inside modals/panels).
     */
    variant?: 'sticky' | 'inline';
    /** Custom offset when sticky (default matches app's PageHeader height). */
    stickyTopClass?: string;
    className?: string;
    /**
     * Stable id prefix. Pass one when you also spread `tabPanelProps(prefix,
     * key)` on the panel, so tab and panel point at each other
     * (`aria-controls` / `aria-labelledby`). Optional — without it the tabs
     * still get generated ids and arrow-key navigation.
     */
    idPrefix?: string;
    /**
     * Sub-steps rather than free navigation (the onboarding assistant): the
     * active tab also gets an orange tint, so it reads as "you are here" next
     * to the disabled ones.
     */
    highlightActive?: boolean;
    /**
     * `default`: small uppercase labels with icons, active in orange.
     * `quiet`: labels as written, no icons, the active one in the text colour
     * over an orange underline — for a page whose tabs are its sections and
     * whose orange is kept for actions (Financiero; Standards.md §20.2c).
     */
    appearance?: 'default' | 'quiet';
}

/** Props for the container of a tab's content, so it is announced as the
 *  panel of that tab. Pair with the same `idPrefix` given to `<PageTabs>`. */
export function tabPanelProps(idPrefix: string, key: string) {
    return {
        role: 'tabpanel' as const,
        id: `${idPrefix}-panel-${key}`,
        'aria-labelledby': `${idPrefix}-tab-${key}`,
    };
}

function PageTabs<K extends string = string>({
    tabs,
    activeTab,
    onChange,
    variant = 'sticky',
    stickyTopClass = 'top-[53px] md:top-[61px]',
    className = '',
    idPrefix,
    highlightActive = false,
    appearance = 'default',
}: PageTabsProps<K>) {
    const quiet = appearance === 'quiet';
    const autoPrefix = useId();
    const prefix = idPrefix ?? autoPrefix;
    const tabId = (key: string) => `${prefix}-tab-${key}`;
    const panelId = (key: string) => `${prefix}-panel-${key}`;

    // Roving tabindex: only the active tab is in the Tab order; arrows, Home
    // and End move between enabled tabs and activate them.
    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
        const enabled = tabs.map((t, i) => ({ t, i })).filter(({ t }) => !t.disabled);
        if (enabled.length === 0) return;
        const pos = enabled.findIndex(({ i }) => i === index);
        let next = -1;
        if (e.key === 'ArrowRight') next = (pos + 1) % enabled.length;
        else if (e.key === 'ArrowLeft') next = (pos - 1 + enabled.length) % enabled.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = enabled.length - 1;
        if (next < 0) return;
        e.preventDefault();
        const target = enabled[next]?.t;
        if (!target) return;
        preserveScroll(() => onChange(target.key));
        document.getElementById(tabId(target.key))?.focus();
    };
    // A strip wider than its box (a phone) scrolls sideways without a bar, so
    // it keeps the active tab in view itself. Only the strip's own scrollLeft
    // moves — never the page — and a strip that fits never moves at all.
    const navRef = useRef<HTMLElement>(null);
    useEffect(() => {
        const nav = navRef.current;
        const btn = nav ? document.getElementById(tabId(activeTab)) : null;
        if (!nav || !btn) return;
        const navBox = nav.getBoundingClientRect();
        const btnBox = btn.getBoundingClientRect();
        const next = revealTabScrollLeft(
            { scrollLeft: nav.scrollLeft, clientWidth: nav.clientWidth, scrollWidth: nav.scrollWidth },
            { offsetLeft: btnBox.left - navBox.left + nav.scrollLeft, offsetWidth: btnBox.width },
        );
        if (next !== nav.scrollLeft) nav.scrollLeft = next;
        // eslint-disable-next-line react-hooks/exhaustive-deps -- tabId only depends on prefix
    }, [activeTab, prefix]);
    const wrapperClasses =
        variant === 'sticky'
            ? `sticky ${stickyTopClass} z-10 bg-background-light dark:bg-background-dark -mx-3 md:-mx-6 px-3 md:px-6 border-b border-border-subtle dark:border-border-subtle-dark`
            : 'border-b border-border-subtle dark:border-border-subtle-dark';

    return (
        <div className={`${wrapperClasses} ${className}`}>
            <nav
                ref={navRef}
                role="tablist"
                className={`no-scrollbar flex overflow-x-auto whitespace-nowrap -mb-px ${quiet ? 'gap-6' : 'gap-1'}`}
            >
                {tabs.map((tab, index) => {
                    const isActive = activeTab === tab.key;
                    const isDisabled = !!tab.disabled;
                    return (
                        <button
                            key={tab.key}
                            type="button"
                            role="tab"
                            id={tabId(tab.key)}
                            aria-selected={isActive}
                            aria-controls={panelId(tab.key)}
                            tabIndex={isActive ? 0 : -1}
                            disabled={isDisabled}
                            onKeyDown={(e) => handleKeyDown(e, index)}
                            onClick={() => !isDisabled && preserveScroll(() => onChange(tab.key))}
                            className={quiet
                                ? `flex items-center gap-1.5 px-0.5 pb-2.5 pt-1 font-display text-sm transition-colors border-b-2
                                    ${
                                        isDisabled
                                            ? 'border-transparent text-slate-400 opacity-40 cursor-not-allowed'
                                            : isActive
                                            ? 'border-primary font-semibold text-text-main dark:text-white'
                                            : 'border-transparent font-medium text-text-sub hover:text-text-main dark:text-slate-400 dark:hover:text-slate-200'
                                    }`
                                : `flex items-center gap-1.5 px-3 py-2.5 text-xs font-bold uppercase tracking-wider transition-all border-b-2
                                ${
                                    isDisabled
                                        ? 'border-transparent text-slate-400 dark:text-slate-500 opacity-40 cursor-not-allowed'
                                        : isActive
                                        ? `border-primary text-primary ${highlightActive ? 'bg-primary/10 rounded-t-lg' : ''}`
                                        : 'border-transparent text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'
                                }`}
                        >
                            {tab.icon && !quiet && (
                                <Icon name={tab.icon} className="!text-[16px]" aria-hidden="true" />
                            )}
                            {tab.label}
                            {tab.dot ? (
                                <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
                            ) : null}
                            {tab.lockLabel ? <PlanLockPill label={tab.lockLabel} /> : null}
                            {tab.count !== undefined && (
                                <span
                                    className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold leading-none
                                    ${
                                        isActive
                                            ? 'bg-primary/10 text-primary'
                                            : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                                    }`}
                                >
                                    {tab.count}
                                </span>
                            )}
                        </button>
                    );
                })}
            </nav>
        </div>
    );
}

export default PageTabs;
